import type {
  EmailTemplate,
  ReceivedEmail,
  SentEmail,
} from "@/domains/communication/api/types";
import {
  SEED_RECEIVED_EMAILS,
  SEED_SENT_EMAILS,
  SEED_TEMPLATES,
} from "@/domains/communication/mocks/templates";

let templates: Array<EmailTemplate> = SEED_TEMPLATES.map((t) => ({
  ...t,
  variables: [...t.variables],
}));
let sentEmails: Array<SentEmail> = SEED_SENT_EMAILS.map((e) => ({
  ...e,
  to: [...e.to],
}));
let receivedEmails: Array<ReceivedEmail> = SEED_RECEIVED_EMAILS.map((e) => ({ ...e }));
let nextEmailCounter = 1000;

function delay<T>(value: T, ms = 180): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export async function fetchTemplates(): Promise<ReadonlyArray<EmailTemplate>> {
  return delay(templates);
}

export async function fetchTemplateById(id: string): Promise<EmailTemplate | undefined> {
  return delay(templates.find((t) => t.id === id));
}

export async function fetchSentEmails(): Promise<ReadonlyArray<SentEmail>> {
  return delay(sentEmails);
}

export async function fetchSentEmailsByCase(
  caseId: string,
): Promise<ReadonlyArray<SentEmail>> {
  return delay(sentEmails.filter((e) => e.caseId === caseId));
}

export async function saveTemplate(template: EmailTemplate): Promise<EmailTemplate> {
  const idx = templates.findIndex((t) => t.id === template.id);
  if (idx === -1) {
    templates = [...templates, template];
  } else {
    templates = [...templates.slice(0, idx), template, ...templates.slice(idx + 1)];
  }
  return delay(template, 120);
}

export async function deleteTemplate(id: string): Promise<void> {
  templates = templates.filter((t) => t.id !== id);
  return delay(undefined, 100);
}

export async function sendEmail(input: Omit<SentEmail, "id" | "sentAt">): Promise<SentEmail> {
  nextEmailCounter += 1;
  const email: SentEmail = {
    ...input,
    id: `em-${input.caseId}-${nextEmailCounter}`,
    sentAt: new Date().toISOString(),
  };
  sentEmails = [email, ...sentEmails];
  return delay(email, 250);
}

export async function fetchReceivedEmails(): Promise<ReadonlyArray<ReceivedEmail>> {
  return delay(receivedEmails);
}

export async function fetchReceivedEmailsByCase(
  caseId: string,
): Promise<ReadonlyArray<ReceivedEmail>> {
  return delay(receivedEmails.filter((e) => e.caseId === caseId));
}
